import { defaultQueryOptions } from 'constants/'
import { useQuery } from 'react-query'
import { QueryOptions } from 'types'
import { useSummaryService } from '../services'
import type { AppointmentScoringRecord, ScoringCategoryKey } from '../services'

const hookName = 'useAppointmentTranscriptScores'

// `/appointment-scorings` paginates; without an explicit page size the server default could be
// smaller than the SUCCESS scorings for the queried appointments, silently dropping older
// records so those entries render no badge. This must comfortably exceed the SUCCESS scorings
// for one customer's completed HAE appointments (a handful, even allowing for regenerations).
const TRANSCRIPT_SCORINGS_PAGE_SIZE = 200

type TranscriptScoreRecord = {
  appointmentGid: string
  categories: Partial<Record<ScoringCategoryKey, number>>
  createdAt: string
  explanations: Partial<Record<ScoringCategoryKey, string>>
  maxTotalScore: number | null
  totalScore: number | null
}

type TranscriptScoresMap = Map<string, TranscriptScoreRecord>

/** Keyed by appointment; keeps the latest record per appointment by `createdAt`. */
const mapScoringsToTranscriptScores = (records: AppointmentScoringRecord[]): TranscriptScoresMap => {
  const map: TranscriptScoresMap = new Map()

  records.forEach(({ appointmentGid, categories, createdAt, explanations, maxTotalScore, totalScore }) => {
    if (!appointmentGid) {
      return
    }

    // `''` is the oldest/unknown sentinel: a record with a real timestamp always replaces it.
    const created = createdAt ?? ''
    const existing = map.get(appointmentGid)

    if (existing && existing.createdAt >= created) {
      return
    }

    map.set(appointmentGid, {
      appointmentGid,
      categories: categories ?? {},
      createdAt: created,
      explanations: explanations ?? {},
      maxTotalScore: maxTotalScore ?? null,
      totalScore: totalScore ?? null,
    })
  })

  return map
}

const useAppointmentTranscriptScores = (appointmentGids: string[], options: QueryOptions = { enabled: true }) => {
  const { getAppointmentScorings } = useSummaryService()
  // Sorted so the query key is stable regardless of source ordering.
  const sortedGids = [...appointmentGids].sort()

  const doQuery = async (): Promise<TranscriptScoresMap> => {
    if (sortedGids.length === 0) {
      return new Map()
    }

    const { data = [] } = await getAppointmentScorings({
      appointmentGid: sortedGids,
      pageSize: TRANSCRIPT_SCORINGS_PAGE_SIZE,
      status: ['SUCCESS'],
    })

    // Truncation must never be silent: a dropped page means missing badges with no signal. A
    // full page is the scoping-independent signal that more SUCCESS records may exist beyond
    // page 0 — `meta.itemsTotal` is avoided here because it can't be trusted to be scoped to
    // the `status: ['SUCCESS']` filter (it may count PROCESSING/FAILED/SKIPPED too).
    if (data.length >= TRANSCRIPT_SCORINGS_PAGE_SIZE) {
      console.warn(
        `${hookName}: received a full page of ${data.length} scorings — more may exist; raise TRANSCRIPT_SCORINGS_PAGE_SIZE (${TRANSCRIPT_SCORINGS_PAGE_SIZE}) or paginate.`
      )
    }

    return mapScoringsToTranscriptScores(data)
  }

  return useQuery([hookName, { appointmentGids: sortedGids }], doQuery, {
    ...defaultQueryOptions,
    ...options,
  })
}

export { mapScoringsToTranscriptScores, useAppointmentTranscriptScores }
export type { TranscriptScoreRecord, TranscriptScoresMap }
